/**
 * A GoTrue error -> the `auth.errors.<key>` a person reads (design section 2). GoTrue's messages are
 * English for developers and are never rendered: the caller logs them with console.warn.
 *
 * `enumerationSafe`: a sign-in link asked for an EXISTING account only (`shouldCreateUser: false`). A
 * "no such account" answer then reads as sent, so the form never tells a stranger which addresses have
 * an account ("if this address has an account, we sent a link").
 */

export const AUTH_ERROR_KEYS = [
  'tooManyEmails',
  'invalidEmail',
  'linkExpired',
  'linkOtherBrowser',
  'otherAccount',
  'weakPassword',
  'samePassword',
  'invalidCredentials',
  'network',
  'generic',
  'oauthCancelled',
  'oauthFailed',
  'oauthUnavailable',
  'identityTaken',
  'otherAccountOAuth',
] as const;
export type AuthErrorKey = (typeof AUTH_ERROR_KEYS)[number];

export interface AuthLikeError {
  code?: string;
  status?: number;
  message?: string;
  name?: string;
}

const LINK_CODES = new Set(['otp_expired', 'access_denied', 'invalid_link', 'flow_state_expired']);
const NO_ACCOUNT_CODES = new Set(['otp_disabled', 'signup_disabled', 'user_not_found']);

function isNetworkError(err: AuthLikeError): boolean {
  return err.name === 'AuthRetryableFetchError' || /Failed to fetch|NetworkError|Load failed|fetch failed/i.test(err.message ?? '');
}

/** The key for `err`, or null when it must read as success (the enumeration-safe cases). */
export function describeAuthError(err: AuthLikeError, opts: { enumerationSafe?: boolean } = {}): AuthErrorKey | null {
  const code = err.code ?? '';
  const message = err.message ?? '';

  if (opts.enumerationSafe && NO_ACCOUNT_CODES.has(code)) return null;
  if (isNetworkError(err)) return 'network';
  if (code === 'over_email_send_rate_limit' || code === 'over_request_rate_limit' || err.status === 429) return 'tooManyEmails';
  if (code === 'email_address_invalid' || (code === 'validation_failed' && /email/i.test(message))) return 'invalidEmail';
  if (LINK_CODES.has(code)) return 'linkExpired';
  if (code === 'weak_password') return 'weakPassword';
  if (code === 'same_password') return 'samePassword';
  if (code === 'invalid_credentials' || /^Invalid login credentials$/i.test(message)) return 'invalidCredentials';
  return 'generic';
}


const OAUTH_UNAVAILABLE_CODES = new Set(['manual_linking_disabled', 'provider_disabled', 'validation_failed']);

/**
 * The key for a refusal of `signInWithOAuth`, `linkIdentity` or `unlinkIdentity` that came back before
 * any redirect (a way in switched off on the server, an identity already on another account).
 */
export function describeOAuthError(err: AuthLikeError): AuthErrorKey {
  const code = err.code ?? '';
  if (isNetworkError(err)) return 'network';
  if (OAUTH_UNAVAILABLE_CODES.has(code)) return 'oauthUnavailable';
  if (code === 'identity_already_exists') return 'identityTaken';
  return 'oauthFailed';
}
